import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../context/BrandingContext';
import { Button } from '../components/ui/Button';

/**
 * Lands a session moved from another hostname.
 *
 * Auth tokens live in localStorage, which is scoped to a single origin, so a
 * tenant moving from its platform subdomain to its own domain would otherwise
 * sign every user out. They arrive here carrying a single-use ticket, which is
 * exchanged for real tokens on this origin.
 *
 * The ticket travels in the URL fragment rather than the query string: fragments
 * are not sent to the server, so it stays out of access logs and Referer headers.
 * It is stripped from the address bar as soon as it is read, and it is single-use
 * server-side regardless.
 */
const SessionHandoff: React.FC = () => {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [failed, setFailed] = useState(false);

  // Redemption spends the ticket, so it must not run twice — React 18 mounts
  // effects twice in development.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const token = params.get('token');

    // Remove the ticket from the address bar before anything else, so it does not
    // survive in history or get shared by a copied URL.
    window.history.replaceState(null, '', window.location.pathname);

    if (!token) {
      setFailed(true);
      return;
    }

    const base = (process.env.REACT_APP_API_URL ?? process.env.REACT_APP_API_BASE_URL ?? '').replace(/\/+$/, '');

    fetch(`${base}/auth/api/auth/session-handoff/redeem`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`redeem failed: ${response.status}`);
        return response.json();
      })
      .then((session) => {
        if (!session?.token) throw new Error('no token in response');

        localStorage.setItem('agentic_auth_token', session.token);
        if (session.refreshToken) {
          localStorage.setItem('agentic_refresh_token', session.refreshToken);
        }

        navigate('/dashboard', { replace: true });
      })
      .catch(() => {
        // Expired, already used, or presented on the wrong host. Signing in
        // normally is the recovery path, so say that rather than showing an error
        // the user cannot act on.
        setFailed(true);
      });
  }, [navigate]);

  const productName = branding.productName;

  return (
    <div className="flex min-h-screen items-center justify-center bg-agentic-white-off p-6">
      <div className="w-full max-w-md text-center">
        {failed ? (
          <>
            <h1 className="text-xl font-semibold text-agentic-text-primary">
              This sign-in link has expired
            </h1>
            <p className="mt-2 text-agentic-text-secondary">
              Sign-in links can only be used once, and expire quickly. Please sign in
              {productName ? ` to ${productName}` : ''} to continue.
            </p>
            <Button tone="primary"
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="mt-6 rounded-xl px-5 py-2.5 text-sm font-medium"
            >
              Go to sign in
            </Button>
          </>
        ) : (
          <p className="text-agentic-text-secondary" role="status">
            Signing you in…
          </p>
        )}
      </div>
    </div>
  );
};

export default SessionHandoff;
